import { useEffect, useEffectEvent, useMemo, useState } from "react";

import { captureException } from "@sentry/react";
import axios from "axios";
import { MARKER_COLORS, TRACK_COLORS } from "config/constants";
import { Routing } from "config/enums/routings";
import equal from "fast-deep-equal";
import { ActionTimeout } from "hooks/useActionTimeout";
import { useTrackStats } from "stores/useTrackStats";
import { useUserWaypoints } from "stores/useUserWaypoints";
import { t } from "i18next";
import L, { LeafletEventHandlerFnMap } from "leaflet";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import { rewriteChartTooltipData } from "stores/genericStore/actions";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import {
  addTrackWaypoint,
  changeShowPlaceholderTrack,
  deleteTrackWaypoint,
  rewriteComputedTrackpoints,
  rewriteComputedTrackpointsWithAttributes,
  rewriteTrackmeta,
  rewriteTrackWaypoints,
  setIsRouting,
} from "stores/routingStore/actions";
import { useRouting } from "stores/routingStore/RoutingContext";
import { ComputedTrackPointsWithAttributes } from "stores/routingStore/state";
import { Coords } from "types/app";
import {
  fetchRoute,
  getCostingOptionsPayload,
  Route,
  RouteRequest,
  RoutingServerError,
} from "utils/api/fetchRoute";
import { smoothDataGauss } from "utils/formatters/gaussianSmoothing";
import {
  showErrorNotification,
  showWarningNotification,
} from "utils/notifications/customNotifications";
import {
  getInsertedWaypointIndex,
  getMissingRouteSegments,
} from "utils/routing/routeGeometry";
import { numericMarkerIcon } from "../Icons";
import { DragStylePolyline } from "./components/DragStylePolyline";
import { MissingRoutePolyline } from "./components/MissingRoutePolyline";
import { RoutablePolyline } from "./components/RoutablePolyline";
import "./RoutingControl.css";

/** The routing server takes at most this many waypoints */
const MAX_WAYPOINTS: Record<Routing, number> = {
  [Routing.auto]: 20,
  [Routing.bicycle]: 50,
  [Routing.pedestrian]: 50,
  [Routing.none]: Infinity,
};

const getMarkerColor = (index: number, count: number) => {
  if (index === 0) return MARKER_COLORS.start;
  if (index === count - 1) return MARKER_COLORS.end;
  return MARKER_COLORS.route;
};

const getMarkerIcon = (index: number, count: number) =>
  numericMarkerIcon(getMarkerColor(index, count) as `#${string}`, index + 1);

/** A waypoint being inserted by dragging the route line, it's added when the mouse button is released */
type InsertedWaypoint = { index: number; latLng: L.LatLng };

/**
 * The time and the speed at each point of the route. The time of each maneuver is spread evenly over its
 * segments, the speed is the average speed of the maneuver.
 */
const getPointsWithAttributes = ({
  coordinates,
  maneuvers,
}: Route): ComputedTrackPointsWithAttributes[] => {
  /** The maneuver that starts at or before the point and continues after it */
  const getManeuverAt = (pointIndex: number) =>
    maneuvers.find((maneuver, index) => {
      const nextManeuver = maneuvers[index + 1];
      if (!nextManeuver) return true;
      return (
        maneuver.beginIndex <= pointIndex &&
        pointIndex < nextManeuver.beginIndex
      );
    })!;

  let totalTimeElapsed = 0;
  return coordinates.map((coords, index) => {
    const currentManeuver = getManeuverAt(index);
    // The segment leading to this point belongs to the maneuver of the previous point
    const segmentManeuver = index > 0 ? getManeuverAt(index - 1) : null;
    const maneuverSegments = segmentManeuver
      ? segmentManeuver.endIndex - segmentManeuver.beginIndex
      : 0;
    totalTimeElapsed +=
      !segmentManeuver || segmentManeuver.time === 0 || maneuverSegments === 0
        ? 0
        : segmentManeuver.time / maneuverSegments;
    const currentSectionAvgSpeed =
      currentManeuver.length === 0 || currentManeuver.time === 0
        ? 0
        : (currentManeuver.length * 1000) / currentManeuver.time;

    return {
      coords,
      // Relative time of each point in seconds (needed for the chart)
      time: totalTimeElapsed,
      speed: currentSectionAvgSpeed,
    };
  });
};

/**
 * Handles displaying and control of routable trackpoints: routes the track's waypoints with the routing server,
 * draws the route and the waypoints' markers, which can be dragged and clicked to be deleted. Dragging the route
 * inserts a waypoint.
 */
export const RoutingControl = ({
  actionTimeout,
}: {
  actionTimeout: ActionTimeout;
}) => {
  const map = useMap();
  const {
    state: {
      trackWaypoints,
      computedTrackpoints,
      routing,
      showPlaceholderTrack,
      trackColor,
      computedTrackpointsWithAttributes,
      costingOptions,
      isRouting,
    },
    dispatch,
    fetchElevations,
  } = useRouting();
  const { updateTrackStatsWithElevations, updateTrackStatsWithTrackMeta } =
    useTrackStats();
  const { isAddingWaypoint } = useUserWaypoints();
  const genericDispatch = useGenericDispatch();
  /** The last found route, drawn on the map */
  const [route, setRoute] = useState<Route | null>(null);
  const [insertedWaypoint, setInsertedWaypoint] =
    useState<InsertedWaypoint | null>(null);
  const trackColorWithDefault = trackColor || TRACK_COLORS.routable;

  /** The request of the route, a string so that it's routed again only when the request changes */
  const requestKey =
    trackWaypoints.length < 2
      ? null
      : JSON.stringify({
          waypoints: trackWaypoints,
          costing: routing,
          costingOptions: getCostingOptionsPayload(routing, costingOptions),
        } satisfies RouteRequest);

  useMapEvents({
    // Adds a waypoint
    click: (e) => {
      if (!actionTimeout.timeoutHasPassed()) return;
      if (isAddingWaypoint) return;

      if (trackWaypoints.length >= MAX_WAYPOINTS[routing]) {
        showWarningNotification({
          message: t(
            "notifications.routing.max_track_point_count_reached.message"
          ),
          title: t("notifications.routing.max_track_point_count_reached.title"),
        });
        return;
      }

      dispatch(addTrackWaypoint([e.latlng.lat, e.latlng.lng]));
    },
    mousemove: (e) => {
      if (insertedWaypoint) {
        setInsertedWaypoint({ ...insertedWaypoint, latLng: e.latlng });
      }
    },
    mouseup: (e) => {
      if (!insertedWaypoint) return;
      // The click that follows mustn't add another waypoint
      actionTimeout.setTime();
      map.dragging.enable();
      setInsertedWaypoint(null);
      const { index } = insertedWaypoint;
      dispatch(
        rewriteTrackWaypoints([
          ...trackWaypoints.slice(0, index),
          [e.latlng.lat, e.latlng.lng],
          ...trackWaypoints.slice(index),
        ])
      );
    },
  });

  // The map mustn't stay undraggable when the control is removed while a waypoint is being inserted
  useEffect(() => () => void map.dragging.enable(), [map]);

  useEffect(() => {
    if (trackWaypoints.length < 2) {
      void fetchElevations();
    }
  }, [fetchElevations, trackWaypoints]);

  // A changed waypoint (added, moved, deleted or restored by undo) is an action after which a map click
  // mustn't add a waypoint right away, e.g. the second click of a double click that zooms the map
  const onWaypointsChanged = useEffectEvent(() => actionTimeout.setTime());
  useEffect(() => {
    onWaypointsChanged();
  }, [trackWaypoints]);

  const onRouteFound = useEffectEvent((foundRoute: Route) => {
    setRoute(foundRoute);
    dispatch(setIsRouting(false), false);

    const trackpoints = foundRoute.coordinates;
    let pointsWithAttributes = getPointsWithAttributes(foundRoute);
    if (
      equal(trackpoints, computedTrackpoints) &&
      equal(computedTrackpointsWithAttributes, pointsWithAttributes)
    ) {
      return;
    }

    // The smoothing needs the attributes of all the points
    const pointsCountLengthRatio =
      pointsWithAttributes.length / foundRoute.totalDistance;
    const kernelSize = 3 + pointsCountLengthRatio / 40;
    const smoothedSpeedData = smoothDataGauss(
      pointsWithAttributes,
      "speed",
      kernelSize
    );
    if (smoothedSpeedData) {
      pointsWithAttributes = pointsWithAttributes.map((point, index) => ({
        ...point,
        speed: smoothedSpeedData[index],
      }));
    }

    const trackMeta = {
      totalDistance: foundRoute.totalDistance,
      trackPointsSpeed: smoothedSpeedData,
      trackPointsTime: {
        datapoints: pointsWithAttributes.map(({ time }) => time),
        // The router gives the time of every point, the first one included
        startPointIndex: 0,
        // The time the route was found, to compute absolute times of the points if needed
        startTimestamp: Date.now(),
      },
      totalTime: foundRoute.totalTime,
    };

    void fetchElevations(trackpoints).then((elevations) => {
      if (elevations) {
        updateTrackStatsWithElevations(elevations, trackpoints);
        updateTrackStatsWithTrackMeta(trackMeta);
      }
    });

    dispatch(
      [
        rewriteComputedTrackpointsWithAttributes(pointsWithAttributes),
        rewriteComputedTrackpoints(trackpoints),
        changeShowPlaceholderTrack(false),
        rewriteTrackmeta(trackMeta),
      ],
      false
    );
  });

  const onRoutingError = useEffectEvent((error: unknown) => {
    // No route is coming, the chart's loader and the disabled saving mustn't wait for it
    setRoute(null);
    dispatch(setIsRouting(false), false);
    showErrorNotification({
      title: t("errors.routing_error.title"),
      message: t("errors.routing_error.text"),
    });

    // Errors of the routing server are reported, timeouts and network errors aren't
    if (error instanceof RoutingServerError) {
      captureException(new Error(error.message), {
        extra: { originalError: error.response },
      });
    }
  });

  const onRoutingStart = useEffectEvent(() => {
    dispatch(setIsRouting(true), false);
  });

  /** Less than two waypoints: there's no route */
  const onNoRoute = useEffectEvent(() => {
    setRoute(null);
    if (computedTrackpoints.length > 0) {
      dispatch(rewriteComputedTrackpoints([]), false);
    }
    if (isRouting) dispatch(setIsRouting(false), false);
  });

  // Routes the waypoints whenever the request changes. The route of an earlier request is no longer needed
  // then, its request is aborted.
  useEffect(() => {
    if (requestKey === null) {
      onNoRoute();
      return undefined;
    }

    const controller = new AbortController();
    onRoutingStart();
    fetchRoute(JSON.parse(requestKey) as RouteRequest, controller.signal).then(
      (foundRoute) => {
        if (!controller.signal.aborted) onRouteFound(foundRoute);
      },
      (error: unknown) => {
        if (!controller.signal.aborted && !axios.isCancel(error)) {
          onRoutingError(error);
        }
      }
    );
    return () => controller.abort();
  }, [requestKey]);

  const markerIcons = useMemo(
    () =>
      trackWaypoints.map((_, index) =>
        getMarkerIcon(index, trackWaypoints.length)
      ),
    [trackWaypoints]
  );

  const getMarkerEventHandlers = (index: number): LeafletEventHandlerFnMap => ({
    dragend: (e) => {
      actionTimeout.setTime();
      const { lat, lng } = (e.target as L.Marker).getLatLng();
      dispatch(
        rewriteTrackWaypoints(
          trackWaypoints.map((point, i): Coords =>
            i === index ? [lat, lng] : point
          )
        )
      );
    },
    click: () => {
      // Without a second waypoint no route is coming, the route's data and the routing loader are cleared
      if (trackWaypoints.length === 2) {
        dispatch(
          rewriteTrackmeta({
            totalDistance: undefined,
            trackPointsSpeed: undefined,
            trackPointsTime: undefined,
            totalTime: undefined,
          })
        );
        genericDispatch(rewriteChartTooltipData(null));
        dispatch(setIsRouting(false), false);
      }

      if (actionTimeout.timeoutHasPassed()) {
        dispatch(deleteTrackWaypoint(index));
      }
    },
  });

  const routeEventHandlers: LeafletEventHandlerFnMap = {
    // Pressing the route starts dragging a new waypoint, it's inserted where the mouse button is released
    mousedown: (e) => {
      if (!route) return;
      L.DomEvent.stop(e);
      map.dragging.disable();
      setInsertedWaypoint({
        index: getInsertedWaypointIndex(
          route.coordinates,
          route.waypoints,
          e.latlng
        ),
        latLng: e.latlng,
      });
    },
  };

  const isRouteShown = route !== null && trackWaypoints.length > 1;

  return (
    <>
      {showPlaceholderTrack && computedTrackpoints.length > 2 && (
        <RoutablePolyline
          color={trackColorWithDefault}
          positions={computedTrackpoints}
        />
      )}
      {isRouteShown && (
        <>
          {getMissingRouteSegments(route.coordinates, route.waypoints).map(
            (segment, index) => (
              <MissingRoutePolyline key={index} positions={segment} />
            )
          )}
          <RoutablePolyline
            color={trackColorWithDefault}
            positions={route.coordinates}
            eventHandlers={routeEventHandlers}
          />
        </>
      )}
      {trackWaypoints.map((point, index) => (
        <Marker
          key={index}
          position={point}
          icon={markerIcons[index]}
          draggable
          eventHandlers={getMarkerEventHandlers(index)}
        />
      ))}
      {insertedWaypoint && (
        <>
          <DragStylePolyline
            positions={[
              trackWaypoints[insertedWaypoint.index - 1],
              insertedWaypoint.latLng,
              trackWaypoints[insertedWaypoint.index],
            ]}
          />
          <Marker
            position={insertedWaypoint.latLng}
            icon={getMarkerIcon(
              insertedWaypoint.index,
              trackWaypoints.length + 1
            )}
            interactive={false}
          />
        </>
      )}
    </>
  );
};
